import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CountUp } from "countup.js";
import Vivus from "vivus";
import settings from 'bundle-text:../settings.module.sass';

export default class Animation {
  animatedItems: NodeListOf<Element>;
  headerHeight: number = parseInt(settings['header-height']);
  scrollGrowTimelines: any;
  scrollPins: any = [];
  countUps: any = [];
  scrollGrowItems: any;
  scrollOffset: number = 40;
  ViewportWatcher: any;

  constructor(viewportwatcher, autoInit=true) {
    this.animatedItems = document.querySelectorAll('[data-animate], [data-pin]');
    if (this.animatedItems.length === 0 || !autoInit) return;

    gsap.registerPlugin(ScrollTrigger);

    this.ViewportWatcher = viewportwatcher;
    this.scrollPinContainers = document.querySelectorAll('[data-pin-container]');

    document.addEventListener("DOMContentLoaded", () => {
      if (this.ViewportWatcher.viewMode === 'standard') {
        this.initScrollGrows();
        this.initScrollPins();
      }
      this.initScrollReveals();
      this.initWipeReveals();
      this.initCountUps();
      this.initVivus();
      this.initResizeWatcher();
    })
  }

  initScrollReveals() {
    let scrollRevealItems = document.querySelectorAll('[data-animate="scroll-reveal"], [data-animate="scroll-reveal-children"] > *');

    scrollRevealItems.forEach(item => {
      ScrollTrigger.create({
        trigger: item,
        start: 'top 90%',
        onEnter: () => {
          item.classList.add('animate')
        }
      });
    })
  }

  initScrollGrows() {
    this.scrollGrowTimelines = Object.create({});
    this.scrollGrowItems = document.querySelectorAll('[data-animate="scroll-grow"] picture');

    this.scrollGrowItems.forEach((item, index) => {
      let scalingFactor = ('scalingFactor' in item.dataset)? parseFloat(item.dataset.scalingFactor) : 1.4;
      let scalingPercentage = `${scalingFactor * 100}%`;

      this.scrollGrowTimelines[index] = gsap.timeline({
        scrollTrigger: {
          trigger: item,
          start: "top 80%",
          end: "bottom top",
          toggleActions: "play none none reverse",
          scrub: true
        }
      });
      this.scrollGrowTimelines[index].fromTo(item, { width: '100%' }, { width: scalingPercentage })
    })
  }

  initScrollPins() {
    this.scrollPinContainers.forEach((container, index) => {
      let pinItem = container.querySelector('[data-pin]');
      let scrollOffset = pinItem.hasAttribute('data-pin-offset') ? parseInt(pinItem.dataset.pinOffset) : this.scrollOffset;
      let scrollBottomOffset = pinItem.hasAttribute('data-pin-bottom-offset') ? parseInt(pinItem.dataset.pinBottomOffset) : 0;
      let topOffset = this.headerHeight + scrollOffset;
      let startSetting = pinItem.hasAttribute('data-pin-center') ? 'top 50%' : `top top+=${topOffset}px`;

      if (pinItem.hasAttribute('data-pin-bottom-space')) container.style.paddingBottom = `${pinItem.offsetHeight}px`;

      this.scrollPins[index] = ScrollTrigger.create({
        trigger: container,
        pin: pinItem,
        start: startSetting,
        end: () => {
          let topOffsetEnd = topOffset + pinItem.offsetHeight + scrollBottomOffset;
          return `bottom top+=${topOffsetEnd}px`;
        },
        pinSpacing: false,
        scrub: true
      });
    })
  }

  initWipeReveals() {
    let wipeRevealItems = document.querySelectorAll('[data-animate="wipe-reveal"]');

    wipeRevealItems.forEach(item => {
      ScrollTrigger.create({
        trigger: item,
        start: 'top 75%',
        onEnter: () => {
          item.classList.add('animate')
        }
      });
    })
  }

  initVivus() {
    let vivusItems = document.querySelectorAll('[data-animate="draw"] svg');

    vivusItems.forEach(item => {
      new Vivus(item, { duration: 70 });
    })
  }

  initCountUps(altCountUpItems=null) {
    this.countUpItems = altCountUpItems ? altCountUpItems : document.querySelectorAll('[data-animate="countup"]');

    this.countUpItems.forEach((item, index) => {
      let counterValue = this.parseValues(Number(item.textContent));
      let startValue = item.hasAttribute('data-countup-start-val') ? item.getAttribute('data-countup-start-val') : 0;

      item.dataset.counterValue = counterValue;
      item.dataset.startValue = startValue;

      var options = {
        decimal: '',
        duration: 2.8,
        separator: '',
        startVal: startValue
      };

      if(!this.isInt(counterValue)) {
        options.decimalPlaces = this.getDecimalCount(counterValue);
        options = this.setSeparators(options)
      } else {
        if (item.dataset.countupSeparator === '') {
          options = this.setSeparators(options)
        }
      }

      this.countUps[index] = new CountUp(item, counterValue, options);

      this.setCountUpItemWidth(item, index);

      ScrollTrigger.create({
        trigger: item,
        start: 'top 90%',
        onEnter: () => this.countUps[index].start()
      });
    });
  }

  setCountUpItemWidth(item, index) {
    item.style.width = 'auto';
    item.textContent = this.countUps[index].formatNumber(item.dataset.counterValue);
    item.style.width = `${item.offsetWidth}px`;
    item.textContent = this.countUps[index].formatNumber(item.dataset.startValue);
  }

  updateCountUpItems() {
    this.countUpItems.forEach((item, index) => {
      this.setCountUpItemWidth(item, index);
    });
  }


  getDecimalCount(num) {
    const numStr = String(num);
    if (numStr.includes('.')) return numStr.split('.')[1].length;
    return 0;
  }

  isInt(val) {
    return val % 1 === 0
  }

  parseValues(val) {
    val = this.isInt(val) ? parseInt(val) : parseFloat(val);
    return val
  }

  setSeparators(options) {
    let isDE = document.documentElement.lang == 'de';
    options.separator = isDE ? '.' : ',';
    options.decimal = isDE ? ',' : '.';
    return options
  }

  initResizeWatcher() {
    window.addEventListener('viewport:resized', () => {
      this.updateCountUpItems();
      ScrollTrigger.refresh()
    });
    window.addEventListener('viewport:resized-to:mobile', () => {
      this.destroyScrollGrowScrollTrigger();
      this.destroyScrollPins();
    });
    window.addEventListener('viewport:resized-to:standard', () => {
      this.initScrollGrows();
      this.initScrollPins();
    });
  }

  destroyScrollGrowScrollTrigger() {
    this.scrollGrowItems.forEach((item, index) => {
      this.scrollGrowTimelines[index].seek(0);
      this.scrollGrowTimelines[index].kill();
    })
    this.scrollGrowTimelines = Object.create({});
  }

  destroyScrollPins() {
    this.scrollPinContainers.forEach((container, index) => {
      container.style.paddingBottom = '0';
      this.scrollPins[index].kill();
    })
    this.scrollPins = [];
  }
}

